import React from 'react';
import { Container, Typography, Box, Grid, TextField, Button, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { toast } from 'react-toastify';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

const notificationBaseUrl = process.env.REACT_APP_NOTIFICATION_BASE_URL;

const toastStyle = {
	style: {
		background: '#04305a',
		color: '#fff',
		border: '1px solid rgba(255, 255, 255, 0.12)',
		borderRadius: '8px',
		fontSize: '0.9rem',
	},
	progressStyle: { background: 'var(--gof-color-accent)' },
};

const Footer = () => {
	const storageBaseUrl = process.env.REACT_APP_STORAGE_BASE_URL;
	const logoUrl = `${storageBaseUrl}/images/shared/logo.navbar.svg`;

	const initialValues = {
		email: '',
	};

	const validationSchema = Yup.object({
		email: Yup.string()
			.email('Некорректный email адрес')
			.matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Некорректный email адрес')
			.required('Обязательно для заполнения'),
	});

	const handleSubmit = async (values, { setSubmitting, setErrors, resetForm }) => {
		try {
			await axios.post(`${notificationBaseUrl}/NewsletterSubscriber`, { email: values.email });
			resetForm();
			toast.success('Подписка успешна!', toastStyle);
		} catch (error) {
			toast.error('Ошибка при отправке запроса. Попробуйте снова.', toastStyle);
		} finally {
			setSubmitting(false);
		}
	};

	const footerLinks = [
		{ label: 'Главная', to: '/' },
		{ label: 'Паттерны', to: '/patterns' },
		{ label: 'Тренажер', to: '/trainer' },
	];

	return (
		<Box
			component="footer"
			sx={{
				bgcolor: 'var(--gof-color-footer)',
				color: '#fff',
				pt: { xs: 6, md: 8 },
				pb: 0,
			}}
		>
			<Container maxWidth="lg">
				<Grid container spacing={{ xs: 4, md: 6 }}>
					{/* Brand + newsletter */}
					<Grid item xs={12} md={6}>
						<Box
							component="img"
							src={logoUrl}
							alt="GOFINSIGHTS logo"
							sx={{
								height: 32,
								width: 'auto',
								mb: 2,
								filter: 'brightness(0) invert(1)',
								opacity: 0.9,
							}}
						/>
						<Typography
							variant="body2"
							sx={{
								color: 'rgba(255, 255, 255, 0.6)',
								lineHeight: 1.7,
								mb: 2.5,
								maxWidth: 360,
							}}
						>
							Справочник паттернов проектирования GoF и
							<br />
							AI-тренажер для разработчиков.
						</Typography>

						<Typography
							variant="body2"
							sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600, mb: 1.5 }}
						>
							Подпишитесь на обновления
						</Typography>
						<Formik
							initialValues={initialValues}
							validationSchema={validationSchema}
							onSubmit={handleSubmit}
						>
							{({ isSubmitting }) => (
								<Form>
									<Box sx={{ display: 'flex', gap: 1, maxWidth: 360 }}>
										<Field
											as={TextField}
											name="email"
											placeholder="email@example.com"
											variant="outlined"
											size="small"
											fullWidth
											helperText={<ErrorMessage name="email" component="span" />}
											sx={{
												'& .MuiOutlinedInput-root': {
													bgcolor: 'rgba(255, 255, 255, 0.06)',
													borderRadius: '8px',
													color: '#fff',
													fontSize: '0.9rem',
													'& fieldset': {
														borderColor: 'rgba(255, 255, 255, 0.12)',
													},
													'&:hover fieldset': {
														borderColor: 'rgba(255, 255, 255, 0.25)',
													},
													'&.Mui-focused fieldset': {
														borderColor: 'var(--gof-color-accent)',
													},
												},
												'& .MuiInputBase-input::placeholder': {
													color: 'rgba(255, 255, 255, 0.35)',
													opacity: 1,
												},
												'& .MuiFormHelperText-root': {
													color: '#f87171',
													mt: 0.5,
												},
											}}
										/>
										<Button
											type="submit"
											variant="contained"
											disabled={isSubmitting}
											sx={{
												bgcolor: 'var(--gof-color-accent)',
												minWidth: 44,
												height: 40,
												borderRadius: '8px',
												flexShrink: 0,
												'&:hover': { bgcolor: 'var(--gof-color-accent-hover)' },
											}}
										>
											<SendOutlinedIcon sx={{ fontSize: 18 }} />
										</Button>
									</Box>
								</Form>
							)}
						</Formik>
						<Typography
							variant="caption"
							sx={{
								color: 'rgba(255, 255, 255, 0.35)',
								display: 'block',
								mt: 1.5,
								maxWidth: 360,
								lineHeight: 1.5,
							}}
						>
							Подписываясь, вы соглашаетесь с нашей Политикой конфиденциальности.
						</Typography>
					</Grid>

					{/* Navigation */}
					<Grid item xs={6} sm={4} md={2}>
						<Typography
							variant="body2"
							sx={{
								color: 'rgba(255, 255, 255, 0.4)',
								fontWeight: 600,
								textTransform: 'uppercase',
								letterSpacing: '0.08em',
								fontSize: '0.75rem',
								mb: 2,
							}}
						>
							Разделы
						</Typography>
						{footerLinks.map((link) => (
							<MuiLink
								key={link.to}
								component={Link}
								to={link.to}
								sx={{
									display: 'block',
									color: 'rgba(255, 255, 255, 0.7)',
									textDecoration: 'none',
									fontSize: '0.9rem',
									py: 0.5,
									transition: 'color 0.2s',
									'&:hover': { color: '#fdb97e' },
								}}
							>
								{link.label}
							</MuiLink>
						))}
					</Grid>

					{/* Contact */}
					<Grid item xs={6} sm={4} md={4}>
						<Typography
							variant="body2"
							sx={{
								color: 'rgba(255, 255, 255, 0.4)',
								fontWeight: 600,
								textTransform: 'uppercase',
								letterSpacing: '0.08em',
								fontSize: '0.75rem',
								mb: 2,
							}}
						>
							Контакты
						</Typography>
						<MuiLink
							href="https://t.me/Gofinsights_bot"
							target="_blank"
							rel="noopener noreferrer"
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								color: 'rgba(255, 255, 255, 0.7)',
								textDecoration: 'none',
								fontSize: '0.9rem',
								py: 0.5,
								transition: 'color 0.2s',
								'&:hover': { color: '#fdb97e' },
							}}
						>
							<TelegramIcon sx={{ fontSize: 18 }} />
							Telegram
						</MuiLink>
						<MuiLink
							href="mailto:support@gofinsights.com"
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								color: 'rgba(255, 255, 255, 0.7)',
								textDecoration: 'none',
								fontSize: '0.9rem',
								py: 0.5,
								transition: 'color 0.2s',
								'&:hover': { color: '#fdb97e' },
							}}
						>
							<EmailOutlinedIcon sx={{ fontSize: 18 }} />
							support@gofinsights.com
						</MuiLink>
					</Grid>

				</Grid>

				{/* Bottom bar */}
				<Box
					sx={{
						mt: { xs: 5, md: 6 },
						py: 3,
						borderTop: '1px solid rgba(255, 255, 255, 0.08)',
						display: 'flex',
						flexDirection: { xs: 'column', sm: 'row' },
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 1.5,
					}}
				>
					<Typography
						variant="caption"
						sx={{
							color: 'rgba(255, 255, 255, 0.3)',
							fontSize: '0.8rem',
						}}
					>
						&copy; {new Date().getFullYear()} GOFINSIGHTS. Все права защищены.
					</Typography>
					<Box sx={{ display: 'flex', gap: 3 }}>
						<MuiLink
							component={Link}
							to="/privacy-policy"
							sx={{
								color: 'rgba(255, 255, 255, 0.3)',
								textDecoration: 'none',
								fontSize: '0.8rem',
								transition: 'color 0.2s',
								'&:hover': { color: 'rgba(255, 255, 255, 0.6)' },
							}}
						>
							Конфиденциальность
						</MuiLink>
						<MuiLink
							component={Link}
							to="/terms-of-service"
							sx={{
								color: 'rgba(255, 255, 255, 0.3)',
								textDecoration: 'none',
								fontSize: '0.8rem',
								transition: 'color 0.2s',
								'&:hover': { color: 'rgba(255, 255, 255, 0.6)' },
							}}
						>
							Условия использования
						</MuiLink>
					</Box>
				</Box>
			</Container>
		</Box>
	);
};

export default Footer;
